import * as React from "react";
import { useDispatch } from "@teamprevent/admin-redux";
import { Button, Grid, Segment } from "semantic-ui-react";

import { Input } from "@neworbit/simpleui-input";

import { setCaseConfidentiality } from "../../actions";
import { isSystemAdmin, isSeniorAdministrator } from "../../../../auth/Authorize";
import { PartialOhCaseDetailModel } from "../../model";

import { CurrentUser } from "../../../../auth";

import { ConfidentialityChangeConfirmModal } from "./ConfidentialityChangeConfirmModal";

interface Props {
    currentUser: CurrentUser;
    ohCase: PartialOhCaseDetailModel;
    customerId: string;
}

const TotallyConfidentialLabel = "Totally Confidential";

export const TotallyConfidentialSection: React.FunctionComponent<Props> = (props: Props) => {
    const [totallyConfidentialModalOpen, setTotallyConfidentialModalOpen] = React.useState<boolean>(false);
    const [changingConfidentiality, setChangingConfidentiality] = React.useState<boolean>(false);

    const dispatch = useDispatch();

    const isChangingCaseConfidentialityAllowed = React.useMemo(() =>
        isSystemAdmin(props.currentUser) || (isSeniorAdministrator(props.currentUser) && !props.ohCase?.totallyConfidential)
    , [props.currentUser, props.ohCase?.totallyConfidential]);

    const onOhCaseTotallyConfidentialChangeConfirm = React.useCallback(async (reason: string) => {
        if (isChangingCaseConfidentialityAllowed && props.customerId && props.ohCase?.id) {
            setChangingConfidentiality(true);
            await dispatch(setCaseConfidentiality(props.customerId, props.ohCase.id, !props.ohCase.totallyConfidential, reason));
            setChangingConfidentiality(false);
            setTotallyConfidentialModalOpen(false);
        }
    }, [dispatch, isChangingCaseConfidentialityAllowed, props.customerId, props.ohCase.id, props.ohCase.totallyConfidential]);

    const openTotallyConfidentialModal = React.useCallback(() => {
        setTotallyConfidentialModalOpen(true);
    }, []);

    const onTotallyConfidentialModalClosed = React.useCallback(() => {
        setTotallyConfidentialModalOpen(false);
    }, []);

    if (isChangingCaseConfidentialityAllowed) {
        return (
            <>
                <Grid columns={2} verticalAlign="middle">
                    <Grid.Row>
                        <Grid.Column width={6}>
                            <Segment loading={changingConfidentiality} basic>
                                <Input.Checkbox
                                    label={TotallyConfidentialLabel}
                                    value={props.ohCase?.totallyConfidential}
                                    disabled
                                />
                            </Segment>
                        </Grid.Column>
                        <Grid.Column width={8} floated="left">
                            <Button
                                content="Change case confidentiality"
                                disabled={!isChangingCaseConfidentialityAllowed || changingConfidentiality}
                                loading={changingConfidentiality}
                                onClick={openTotallyConfidentialModal}
                                size="small"
                            />
                        </Grid.Column>
                    </Grid.Row>
                </Grid>
                <ConfidentialityChangeConfirmModal
                    isOpen={totallyConfidentialModalOpen}
                    onClose={onTotallyConfidentialModalClosed}
                    caseId={props.ohCase!.caseId!}
                    onConfirm={onOhCaseTotallyConfidentialChangeConfirm}
                />
            </>
        );
    }

    return (
        <Segment loading={changingConfidentiality} basic>
            <Input.Checkbox
                label={TotallyConfidentialLabel}
                value={props.ohCase?.totallyConfidential}
                disabled
            />
        </Segment>
    );
};
